import { ZodType, ZodSafeParseResult, output } from 'zod';

export type CreateZodValidatorFnParams = {
  validSortKeys?: string[];
};

/**
 * Validate an object against a Zod schema using `safeParse`.
 * Returns a `ZodSafeParseResult` typed to the schema's output.
 */
type ValidateZodObjectArgs<T extends ZodType<any>> = {
  value: any;
  validator: T;
};

type ValidateZodObjectReturn<T extends ZodType<any>> = ZodSafeParseResult<output<T>>;

export function validateZodObject<T extends ZodType<any>>({
  value,
  validator,
}: ValidateZodObjectArgs<T>): ValidateZodObjectReturn<T> {
  const result = validator.safeParse(value);
  return result;
}

/**
 * Process validation result
 *
 * Process a Zod safe parse result into a normalized shape.
 * - On success: returns the parsed data as `validListFilter` with an empty `badKeys` array.
 * - On failure: returns an empty `validListFilter` and a deduplicated `badKeys` array
 *   containing the top-level keys that failed validation.
 *
 */

type ProcessValidationResultArgs<T> = {
  parseResult: ZodSafeParseResult<T>;
};

export type ProcessValidationResultReturn<T = any> = {
  validListFilter: T;
  badKeys: string[];
};

export function processValidationResult<T = any>({
  parseResult,
}: ProcessValidationResultArgs<T>): ProcessValidationResultReturn<T> {
  if (parseResult.success) {
    const response: ProcessValidationResultReturn<T> = {
      validListFilter: parseResult?.data || ({} as T),
      badKeys: [],
    };
    return response;
  } else {
    // Extract error keys from zod issues
    const badKeys = Array.from(
      new Set(
        parseResult.error.issues.flatMap((err) =>
          err.path.map((pathElement) => String(pathElement)),
        ),
      ),
    );
    const response: ProcessValidationResultReturn<T> = {
      validListFilter: {} as T,
      badKeys: badKeys,
    };
    return response;
  }
}
